/** Platzhalter für Textvorlagen – Vorschau mit Beispieldaten. */

import { applyCountAgreement } from "@/lib/countAgreement";
import { applyTemplateConditions } from "@/lib/templateConditions";

export interface PlaceholderDoc {
  key: string;
  description: string;
}

/** Sichtbare Bezeichnung im Text und auf den Einfüge-Schaltflächen. */
export const MAIL_TOKEN_LABELS: Record<string, string> = {
  salutation: "Mail-Begrüßung",
  guest_name: "Voller Name",
  guest_first_name: "Vorname",
  guest_last_name: "Nachname",
  guest_email: "E-Mail",
  guest_phone: "Telefon",
  guest_company: "Firma",
  guest_street: "Straße",
  guest_postal_code: "Postleitzahl",
  guest_city: "Ort",
  guest_country: "Land",
  booking_number: "Buchungsnummer",
  booking_status: "Status",
  arrival_date: "Anreise",
  departure_date: "Abreise",
  nights: "Nächte",
  days_until_arrival: "Tage bis Anreise",
  days_until_departure: "Tage bis Abreise",
  today: "Heute",
  pitch_label: "Platz",
  category_name: "Platzkategorie",
  location: "Platz oder Kategorie",
  primary_traveler_name: "Hauptreisende/r",
  check_in_notice: "Hinweis zur Anreise",
  adults: "Erwachsene",
  children: "Kinder",
  dogs: "Hunde",
  persons: "Personen",
  vehicle_type: "Fahrzeug",
  vehicle_length_m: "Fahrzeuglänge",
  license_plate: "Kennzeichen",
  booking_notes: "Notiz",
  booking_details: "Buchungsdaten",
  booking_changes: "Änderungen",
  price_breakdown: "Preisaufschlüsselung",
  total_amount: "Gesamtbetrag",
  stay_price: "Aufenthaltspreis",
  open_amount: "Offener Betrag",
  company_name: "Name des Platzes",
  invoice_number: "Rechnungsnummer",
  issue_date: "Rechnungsdatum",
  due_date: "Fälligkeit",
  reminder_level: "Mahnstufe",
  button_booking: "Zur Buchung",
  button_shop: "Zum Shop",
  completion_url: "Link zur Buchung",
  check_in_url: "Link zum Check-in",
  hold_until: "Frist",
  login_code: "Anmeldecode",
  code_valid_minutes: "Minuten gültig",
  delivery_date: "Liefertag",
  delivery_weekday: "Wochentag",
  extra_cars: "Zusätzliche Autos",
  extra_motorbikes: "Zusätzliche Motorräder",
  companion_1: "Mitreisende/r 1",
  companion_2: "Mitreisende/r 2",
  companion_3: "Mitreisende/r 3",
  user_name: "Mein Name",
  user_first_name: "Mein Vorname",
  user_email: "Meine E-Mail",
};

/** Person, die die Mail abschickt. Für die Grußformel, nicht für PDF-Texte. */
export const SENDER_PLACEHOLDERS: PlaceholderDoc[] = [
  {
    key: "user_name",
    description:
      "Voller Name der angemeldeten Person. Bei automatischen Mails ohne Anmeldung bleibt er leer.",
  },
  { key: "user_first_name", description: "Vorname der angemeldeten Person." },
  { key: "user_email", description: "E-Mail der angemeldeten Person." },
];

export function senderPreviewValues(person: { name?: string; email?: string } | null): Record<string, string> {
  const name = (person?.name || "").trim();
  const email = (person?.email || "").trim();
  const first = name && !name.includes("@") ? (name.split(/\s+/)[0] ?? "") : "";
  return {
    user_name: name || "Anna Beispiel",
    user_first_name: first || "Anna",
    user_email: email || "anna@example.com",
  };
}

export function mailTokenLabel(key: string): string {
  return MAIL_TOKEN_LABELS[key] ?? key;
}

const GREETING_DOC: PlaceholderDoc = {
  key: "salutation",
  description:
    "Mail-Begrüßung: „Sehr geehrter Herr …“ oder „Sehr geehrte Frau …“, je nach Anrede des Gastes. Das Komma setzen Sie dahinter.",
};

export const INVOICE_PLACEHOLDERS: PlaceholderDoc[] = [
  GREETING_DOC,
  { key: "guest_name", description: "Name des Gastes" },
  { key: "invoice_number", description: "Rechnungsnummer" },
  { key: "issue_date", description: "Rechnungsdatum (TT.MM.JJJJ)" },
  { key: "due_date", description: "Fälligkeitsdatum" },
  { key: "total_amount", description: "Rechnungsbetrag inkl. Währung" },
  { key: "open_amount", description: "Offener Betrag" },
  { key: "company_name", description: "Firmenname aus Betriebsdaten" },
  { key: "booking_number", description: "Buchungsnummer (falls verknüpft)" },
  { key: "button_booking", description: "Schaltfläche zur Buchungsseite" },
  { key: "button_shop", description: "Schaltfläche zum Shop" },
];

export const BOOKING_PLACEHOLDERS: PlaceholderDoc[] = [
  GREETING_DOC,
  { key: "guest_name", description: "Name des Gastes" },
  { key: "guest_first_name", description: "Vorname des Gastes" },
  { key: "guest_last_name", description: "Nachname des Gastes" },
  { key: "guest_email", description: "E-Mail des Gastes" },
  { key: "guest_phone", description: "Telefonnummer des Gastes" },
  { key: "guest_street", description: "Straße des Gastes" },
  { key: "guest_postal_code", description: "Postleitzahl des Gastes" },
  { key: "guest_city", description: "Ort des Gastes" },
  { key: "guest_country", description: "Land des Gastes" },
  { key: "booking_number", description: "Buchungsnummer" },
  { key: "arrival_date", description: "Anreisedatum" },
  { key: "departure_date", description: "Abreisedatum" },
  { key: "nights", description: "Anzahl Nächte" },
  { key: "pitch_label", description: "Stellplatz-Bezeichnung" },
  { key: "category_name", description: "Platzkategorie" },
  { key: "location", description: "Stellplatz (nur bei festem Platz) oder Kategorie" },
  { key: "primary_traveler_name", description: "Name des Hauptreisenden" },
  { key: "check_in_notice", description: "Hinweis zu Bestätigung und Ausweis bei Anreise" },
  { key: "adults", description: "Erwachsene" },
  { key: "children", description: "Kinder" },
  { key: "dogs", description: "Hunde" },
  { key: "vehicle_type", description: "Fahrzeug / Anreiseart" },
  { key: "vehicle_length_m", description: "Fahrzeuglänge in Metern" },
  { key: "booking_notes", description: "Nachricht / Anfahrtshinweise" },
  {
    key: "booking_details",
    description: "Alle Buchungsdaten als Fließtext",
  },
  {
    key: "booking_changes",
    description: "Abweichungen von der ursprünglichen Anfrage (Kategorie, Umplanung, Preis)",
  },
  {
    key: "price_breakdown",
    description: "Preisaufschlüsselung wie am Ende der Buchungsstrecke",
  },
  { key: "total_amount", description: "Gesamtbetrag inkl. Währung" },
  { key: "company_name", description: "Firmenname aus Betriebsdaten" },
  { key: "button_booking", description: "Schaltfläche zur Buchungsseite" },
  { key: "button_shop", description: "Schaltfläche zum Shop" },
];

export const EMAIL_LAYOUT_PLACEHOLDERS: PlaceholderDoc[] = [
  { key: "logo", description: "Logo aus Betriebsdaten (wird automatisch eingefügt)" },
  { key: "salutation", description: "Anredezeile über dem Text, z. B. „Sehr geehrter Herr Müller,“" },
  { key: "body", description: "Haupttext aus Ihrer Textvorlage" },
  { key: "extra", description: "Zusatzinhalt (Buchungsdetails, Rechnungsdaten usw.)" },
  { key: "signature", description: "Grußformel mit Firmenname" },
];

export const DUNNING_PLACEHOLDERS: PlaceholderDoc[] = [
  GREETING_DOC,
  { key: "guest_name", description: "Name des Gastes" },
  { key: "invoice_number", description: "Rechnungsnummer" },
  { key: "due_date", description: "Ursprüngliches Fälligkeitsdatum" },
  { key: "open_amount", description: "Offener Betrag" },
  { key: "company_name", description: "Firmenname" },
  { key: "reminder_level", description: "Mahnstufe (1, 2, …)" },
  { key: "button_booking", description: "Schaltfläche zur Buchungsseite" },
  { key: "button_shop", description: "Schaltfläche zum Shop" },
];

const SAMPLE_VALUES: Record<string, string> = {
  salutation: "Sehr geehrter Herr Mustermann",
  guest_name: "Max Mustermann",
  guest_first_name: "Max",
  guest_last_name: "Mustermann",
  guest_email: "max@example.com",
  guest_phone: "0170 1234567",
  guest_street: "Musterstraße 1",
  guest_postal_code: "60311",
  guest_city: "Frankfurt",
  guest_country: "DE",
  booking_number: "B-2026-00042",
  invoice_number: "R-2026-00123",
  arrival_date: "01.07.2026",
  departure_date: "08.07.2026",
  nights: "7",
  pitch_label: "Platz 12",
  category_name: "Komfort",
  location: "Komfort",
  primary_traveler_name: "Max Mustermann",
  check_in_notice:
    "Bitte halten Sie bei der Anreise diese Buchungsbestätigung sowie einen gültigen Ausweis von Max Mustermann bereit.",
  adults: "2",
  children: "1",
  dogs: "0",
  vehicle_type: "Wohnmobil",
  vehicle_length_m: "7.5",
  booking_notes: "GPS: 50°08'17''N ; 8°46'55''E",
  booking_details:
    "Ihre Buchungsdaten:\n\nMax Mustermann\nE-Mail: max@example.com\n…",
  booking_changes:
    "Abweichungen von Ihrer Anfrage:\n• Platzkategorie: statt „Standard“ bieten wir „Premium“ an.",
  price_breakdown:
    "Geschätzter Gesamtpreis: 319,50 €\n9 Nächte · inkl. MwSt.\n\nPremiumplatz / Nacht\n1 × 9 Nächte × 19,00 € · inkl. MwSt.\n171,00 €",
  total_amount: "420,00 EUR",
  open_amount: "210,00 EUR",
  issue_date: "25.05.2026",
  due_date: "08.06.2026",
  // Fallback, solange der hinterlegte Platzname noch nicht geladen ist.
  company_name: "Sonnencamping GmbH",
  reminder_level: "1",
  user_name: "Anna Beispiel",
  user_first_name: "Anna",
  user_email: "anna@example.com",
  completion_url: "https://booking.example/complete/abc",
  check_in_url: "https://booking.example/comfort-check-in/abc",
  hold_until: "02.11.2026 um 18:00 Uhr",
  login_code: "482913",
  code_valid_minutes: "15",
  delivery_date: "03.11.2026",
  delivery_weekday: "Dienstag",
  extra_cars: "0",
  extra_motorbikes: "0",
  companion_1: "Anna Mustermann",
  companion_2: "",
  companion_3: "",
};

export function mailPreviewValues(overrides?: Record<string, string>): Record<string, string> {
  return { ...SAMPLE_VALUES, ...overrides };
}

export function applyPlaceholderPreview(text: string, overrides?: Record<string, string>): string {
  if (!text.trim()) return "";
  const values = mailPreviewValues(overrides);
  const conditioned = applyTemplateConditions(text, values);
  const agreed = applyCountAgreement(conditioned, values);
  return agreed.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match, key: string) => {
    return values[key] ?? match;
  });
}
